import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type { GuestAccessPurpose, GuestOnCase } from "@thalamos/haven-common";
import { hasInvolvementEnded, isInvolvementLive } from "@thalamos/haven-common";

import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import { ActionTableSection } from "../ActionTable/ActionTable.js";

const calendarDate = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

/** The purposes a guest may hold access for, in the order the section lists them. */
const guestAccessPurposes: readonly GuestAccessPurpose[] = [
  "read_shared_assessments",
  "complete_assessment",
];

/**
 * What a reader may do with each guest's row. Details is offered where its
 * handler is given and `mayOpenDetails` holds of the guest; End, on every guest
 * not yet ended, where its handler is given.
 */
export type CaseGuestActions = {
  mayOpenDetails: (guest: GuestOnCase) => boolean;
  onOpenDetails?: (guest: GuestOnCase) => Promisable<void>;
  onEnd?: (guest: GuestOnCase) => Promisable<void>;
};

/** When a guest stops reaching anything: their ending, or else their end date. */
const untilOf = ({ endedAt, expiresAt }: GuestOnCase): Date | null =>
  endedAt ?? expiresAt;

const AccessCount = ({ guest }: { guest: GuestOnCase }) => {
  const { t } = useTranslation();

  return match(guest.access)
    .with({ kind: "read_shared_assessments" }, ({ assessmentCount }) =>
      t("caseGuests.access.read_shared_assessments", {
        count: assessmentCount,
      }),
    )
    .with({ kind: "complete_assessment" }, ({ sentAt }) =>
      sentAt === null
        ? t("caseGuests.access.notSent")
        : t("caseGuests.access.sent"),
    )
    .exhaustive();
};

/** Ended, expired, or nothing for a guest whose access still runs. */
const StateChip = ({ guest }: { guest: GuestOnCase }) => {
  const { t } = useTranslation();

  if (hasInvolvementEnded(guest)) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={t("caseGuests.ended")}
        sx={{ ml: 1 }}
      />
    );
  }

  return isInvolvementLive(guest) ? null : (
    <Chip
      size="small"
      variant="outlined"
      label={t("access.caseInvolvement.expired")}
      sx={{ ml: 1 }}
    />
  );
};

const GuestActions = ({
  guest,
  actions,
}: {
  guest: GuestOnCase;
  actions: CaseGuestActions | undefined;
}) => {
  const { t } = useTranslation();
  const onOpenDetails = actions?.onOpenDetails;
  const onEnd = actions?.onEnd;

  return (
    <>
      {onOpenDetails === undefined || !actions?.mayOpenDetails(guest) ? null : (
        <Button
          size="small"
          variant="outlined"
          aria-label={t("caseGuests.detailsOf", { name: guest.name })}
          onClick={() => {
            void onOpenDetails(guest);
          }}
        >
          {t("caseGuests.details")}
        </Button>
      )}
      {/* An ended guest keeps their row, with Details only. */}
      {onEnd === undefined || hasInvolvementEnded(guest) ? null : (
        <Button
          size="small"
          variant="outlined"
          color="error"
          aria-label={t("endGuestAccess.openOf", { name: guest.name })}
          onClick={() => {
            void onEnd(guest);
          }}
        >
          {t("access.caseInvolvement.end")}
        </Button>
      )}
    </>
  );
};

const RoleHeading = ({ purpose }: { purpose: GuestAccessPurpose }) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="body2" fontWeight="medium">
        {t(`caseGuests.role.${purpose}`)}
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        {t("access.caseInvolvement.anyNumber")}
      </Typography>
    </>
  );
};

const HeldBy = ({ guest }: { guest: GuestOnCase }) => (
  <>
    <Typography variant="body2" color="inherit">
      {guest.name}
    </Typography>
    {guest.jobTitle === null ? null : (
      <Typography variant="caption" display="block" color="text.secondary">
        {guest.jobTitle}
      </Typography>
    )}
  </>
);

/** Ruled off only after a role's last guest, as `CaseInvolvement`'s roles are. */
const runOn = { "& > td, & > th": { borderBottom: 0 } } as const;

const GuestTableRows = ({
  purpose,
  guests,
  actions,
}: {
  purpose: GuestAccessPurpose;
  guests: readonly GuestOnCase[];
  actions: CaseGuestActions | undefined;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <TableRow sx={guests.length > 0 ? runOn : undefined}>
        <TableCell component="th" scope="row">
          <RoleHeading purpose={purpose} />
        </TableCell>
        <TableCell colSpan={4}>
          {guests.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("access.caseInvolvement.none")}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell />
      </TableRow>
      {guests.map((guest, index) => {
        const until = untilOf(guest);

        return (
          <TableRow
            key={guest.involvementId}
            sx={[
              hasInvolvementEnded(guest) && {
                "& td": { color: "text.secondary" },
              },
              index < guests.length - 1 && runOn,
            ]}
          >
            <TableCell component="th" scope="row">
              <Box component="span" sx={VISUALLY_HIDDEN}>
                {t(`caseGuests.role.${purpose}`)}
              </Box>
            </TableCell>
            <TableCell>
              <HeldBy guest={guest} />
            </TableCell>
            <TableCell>
              <AccessCount guest={guest} />
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {calendarDate.format(guest.startedAt)}
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {until === null ? null : calendarDate.format(until)}
              <StateChip guest={guest} />
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <GuestActions guest={guest} actions={actions} />
              </Stack>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
};

const GuestBlock = ({
  purpose,
  guests,
  actions,
}: {
  purpose: GuestAccessPurpose;
  guests: readonly GuestOnCase[];
  actions: CaseGuestActions | undefined;
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={1} sx={{ py: 1.5 }}>
      <Box>
        <RoleHeading purpose={purpose} />
      </Box>
      {guests.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("access.caseInvolvement.none")}
        </Typography>
      ) : null}
      {guests.map((guest) => {
        const until = untilOf(guest);

        return (
          <Stack
            key={guest.involvementId}
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              pl: 1.5,
              borderLeft: 2,
              borderColor: "divider",
              color: hasInvolvementEnded(guest) ? "text.secondary" : undefined,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <HeldBy guest={guest} />
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                {[
                  <AccessCount key="access" guest={guest} />,
                  " · ",
                  t("access.caseInvolvement.sinceDate", {
                    date: calendarDate.format(guest.startedAt),
                  }),
                  ...(until === null
                    ? []
                    : [
                        " · ",
                        t("access.caseInvolvement.untilDate", {
                          date: calendarDate.format(until),
                        }),
                      ]),
                ]}
              </Typography>
              <StateChip guest={guest} />
            </Box>
            <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
              <GuestActions guest={guest} actions={actions} />
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

export type CaseGuestsProps = {
  /** Undefined until the guests have been read, which is not the same as none. */
  guests: readonly GuestOnCase[] | undefined;
  failed: boolean;
  actions?: CaseGuestActions | undefined;
  /** Laid out one guest beneath another, for a narrow width. */
  stacked: boolean;
};

/**
 * The guests on a person's case, in their two roles: who each is, what they were given, and when their access runs. A
 * guest whose access has ended stays listed, greyed, so the case keeps a
 * record of who was let in.
 */
export const CaseGuests = ({
  guests,
  failed,
  actions,
  stacked,
}: CaseGuestsProps) => {
  const { t } = useTranslation();
  const title = t("caseGuests.title");

  if (failed) {
    return (
      <Stack spacing={1} component="section" aria-label={title}>
        <Typography variant="subtitle1" component="h3">
          {title}
        </Typography>
        <Alert severity="error">{t("caseGuests.loadFailed")}</Alert>
      </Stack>
    );
  }

  if (guests === undefined) {
    return null;
  }

  const roles = guestAccessPurposes.map((purpose) => ({
    purpose,
    guests: guests.filter((guest) => guest.access.kind === purpose),
  }));

  if (stacked) {
    return (
      <Stack component="section" aria-label={title}>
        <Typography variant="subtitle1" component="h3" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Divider />
        <Stack divider={<Divider />}>
          {roles.map((role) => (
            <GuestBlock key={role.purpose} {...role} actions={actions} />
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <ActionTableSection
      title={title}
      columns={[
        t("access.caseInvolvement.column.role"),
        t("access.caseInvolvement.column.heldBy"),
        t("caseGuests.column.access"),
        t("access.caseInvolvement.column.since"),
        t("access.caseInvolvement.column.until"),
      ]}
      actionsLabel={t("access.caseInvolvement.column.actions")}
    >
      {roles.map((role) => (
        <GuestTableRows key={role.purpose} {...role} actions={actions} />
      ))}
    </ActionTableSection>
  );
};
